import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck } from "lucide-react";
import { BLOG_POSTS, SITE } from "@/lib/blog";

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

export default function Blog() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Texas Business Litigation Insights — TexasBusinessDispute.com";

    // Meta description
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (created) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute(
      "content",
      "Long-form guides on Texas business litigation by Reza Chawla / Prefcap Law — partner disputes, securities fraud, contract litigation, energy & oil and gas, and firm matching."
    );

    // ItemList structured data so search & LLM crawlers see the blog index
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-blog-jsonld", "index");
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Texas Business Litigation Insights",
      url: `${SITE.url}/blog`,
      publisher: {
        "@type": "LegalService",
        name: SITE.legalEntity,
        url: SITE.url,
        areaServed: "Texas",
      },
      blogPost: BLOG_POSTS.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        description: p.description,
        datePublished: p.date,
        author: { "@type": "Person", name: SITE.attorney },
        url: `${SITE.url}/blog/${p.slug}`,
      })),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevDesc !== null) meta.setAttribute("content", prevDesc);
      ld.remove();
    };
  }, []);

  return (
    <div data-testid="blog-index-page">
      {/* Announcement */}
      <div className="ann-bar" data-testid="announcement-bar">
        Attorney advertisement · Prefcap Law (Houston, Texas) &nbsp;·&nbsp;
        <Link to="/#intake">Begin your assessment →</Link>
      </div>

      {/* Nav */}
      <nav className="nav" data-testid="main-nav">
        <div className="container-tx nav-inner">
          <Link to="/" className="brand" data-testid="brand-logo" style={{ textDecoration: "none" }}>
            <div className="brand-mark">tx</div>
            <div>
              <div className="brand-name">TexasBusinessDispute.com</div>
              <div className="brand-sub">Prefcap Law · Houston, Texas</div>
            </div>
          </Link>
          <div className="nav-links">
            <Link className="nav-link" to="/#how" data-testid="nav-how">Process</Link>
            <Link className="nav-link" to="/#practice" data-testid="nav-practice">Practice</Link>
            <Link className="nav-link" to="/blog" data-testid="nav-blog">Insights</Link>
          </div>
          <div className="nav-actions">
            <a className="nav-phone" href="tel:+17132912875" data-testid="nav-phone-cta">
              <Phone size={13} /> (713) 291-2875
            </a>
            <Link className="nav-cta" to="/#intake" data-testid="nav-cta-btn">
              Free Case Assessment <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="blog-header">
        <div className="container-tx">
          <div className="eyebrow copper">Insights</div>
          <h1 className="serif blog-header-h">
            Texas business litigation,<br /><em>in plain English.</em>
          </h1>
          <p className="blog-header-sub">
            Working guides written by Reza Chawla (Prefcap Law) for business owners, executives, and operators weighing whether — and how — to pursue a Texas legal matter. No filler. No sales pitch.
          </p>
        </div>
      </header>

      {/* Posts list */}
      <section className="blog-list-section">
        <div className="container-tx">
          <ul className="blog-list" data-testid="blog-list">
            {BLOG_POSTS.map((p, i) => (
              <li key={p.slug} className="blog-list-item" data-testid={`blog-card-${p.slug}`}>
                <Link to={`/blog/${p.slug}`} className="blog-list-link">
                  <div className="blog-list-meta">
                    <span className="blog-list-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="blog-list-cat">{p.category}</span>
                    <span className="blog-list-date">{formatDate(p.date)}</span>
                  </div>
                  <h2 className="serif blog-list-title">{p.title}</h2>
                  <p className="blog-list-desc">{p.description}</p>
                  <span className="blog-list-read">
                    Read the guide <ArrowRight size={13} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="bottom-cta" data-testid="blog-bottom-cta">
        <div className="container-tx">
          <h2 className="serif">A specific matter?<br /><em>Have it assessed.</em></h2>
          <p>Confidential intake. No obligation. Three minutes.</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link className="nav-cta" style={{ fontSize: 14, padding: "14px 32px" }} to="/#intake" data-testid="blog-cta-btn">
              Begin Free Assessment <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container-tx">
          <div className="footer-grid">
            <div>
              <div className="brand" style={{ marginBottom: 14 }}>
                <div className="brand-mark">tx</div>
                <div>
                  <div className="brand-name" style={{ color: "#fbf8f3" }}>TexasBusinessDispute.com</div>
                  <div className="brand-sub">Prefcap Law · Houston, Texas</div>
                </div>
              </div>
              <p>Texas attorney-operated business litigation intake and firm matching service.</p>
              <p style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <ShieldCheck size={14} style={{ color: "var(--copper-soft)" }} /> Confidential · No obligation
              </p>
            </div>
            <div>
              <h4>Insights</h4>
              <ul>
                {BLOG_POSTS.map((p) => (
                  <li key={p.slug}><Link style={{ color: "inherit", textDecoration: "none" }} to={`/blog/${p.slug}`}>{p.title}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <h4>Quick Links</h4>
              <ul>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/#how">How it works</Link></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/#practice">Practice areas</Link></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/blog">Insights</Link></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/admin/login" data-testid="footer-admin-link">Admin</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p style={{ marginBottom: 12 }}>
              Referral fee disclosure. TexasBusinessDispute.com is a trade name of Prefcap Law (principal office: Houston, Texas), operated by lead attorney Reza Chawla. If a referral or any other fee is paid, in accordance with the Texas Disciplinary Rules of Professional Conduct (including Rule 1.04(f)), the client will be informed in writing of (i) the identities of the lawyers and firms involved, (ii) the division of fees between them, and (iii) the responsibility each lawyer is assuming. All such terms are documented in the client engagement letter for the matter, and the client's written consent will be obtained before any referral is finalized and before any fee is shared.
            </p>
            © {new Date().getFullYear()} TexasBusinessDispute.com · Prefcap Law (Houston, Texas). Attorney advertisement.
          </div>
        </div>
      </footer>
    </div>
  );
}
